import { useEffect } from "react";

interface UseNavigationWarningsOptions {
  enabled: boolean; // Whether to show navigation warnings
  onNavigationAttempt?: (href: string) => void; // Called when user tries to navigate
  disableBeforeUnload?: boolean; // When true, disables beforeunload warning
  navigationConfirmed?: boolean; // When true (via module-level flag), allows navigation
}

/**
 * Module-level flag to disable beforeunload warning when user confirms via custom modal.
 * This is checked synchronously in beforeunload handler, so state updates won't work.
 */
let navigationConfirmedFlag = false;

/**
 * External helper to set navigation confirmed flag.
 * Called by parent components when user confirms navigation via custom modal.
 */
export function setNavigationConfirmed(value: boolean) {
  navigationConfirmedFlag = value;
}

/**
 * Hook to warn users about losing recordings when navigating away.
 * Intercepts both browser navigation (beforeunload) and in-app link clicks.
 */
export function useNavigationWarnings({
  enabled,
  onNavigationAttempt,
  disableBeforeUnload = false,
}: UseNavigationWarningsOptions) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    /**
     * Handle browser navigation (beforeunload event).
     * Shows native browser warning when user tries to close tab, refresh, etc.
     */
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Don't show browser warning if user already confirmed via custom modal
      // Check module-level flag for synchronous access (React state is async)
      if (navigationConfirmedFlag || disableBeforeUnload) {
        return;
      }

      // Modern browsers ignore custom messages and show their own
      // But we still need to return a string to trigger the warning
      e.preventDefault();
      e.returnValue = "You have a recording in progress.\\nLeave this page and discard it?";
      return e.returnValue;
    };

    /**
     * Handle in-app navigation (intercept link clicks).
     * Prevents navigation and shows custom modal or window.confirm.
     */
    const handleLinkClick = (e: MouseEvent) => {
      // Find the closest anchor element
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");

      // Only intercept if it's a navigation link
      if (anchor && anchor.href) {
        const href = anchor.getAttribute("href");
        const isExternal = anchor.target === "_blank" || anchor.hasAttribute("download");
        const isHash = href?.startsWith("#");

        // Don't intercept external links, hash links, or links without href
        if (isExternal || isHash || !href || href === "#") {
          return;
        }

        // Check if it's a same-origin navigation
        try {
          const url = new URL(anchor.href, window.location.origin);
          const currentUrl = new URL(window.location.href);

          // Only intercept if navigating to a different path
          if (url.origin === currentUrl.origin && url.pathname !== currentUrl.pathname) {
            // Use custom modal if provided, otherwise fall back to window.confirm
            if (onNavigationAttempt) {
              e.preventDefault();
              e.stopPropagation();
              // The parent will set navigationConfirmedFlag when user confirms
              onNavigationAttempt(href);
              return false;
            } else {
              // Fallback to window.confirm for compatibility
              const confirmed = window.confirm(
                "You have a recording in progress.\\nLeaving now will discard it."
              );

              if (!confirmed) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            }
          }
        } catch (err) {
          // Invalid URL, allow default behavior
          console.warn("Could not parse link URL:", err);
        }
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleLinkClick, true); // Use capture phase to intercept early

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [enabled, onNavigationAttempt, disableBeforeUnload]);
}
